const DEFAULT_LOCALE = 'ru-RU';

/** Показывает календарные дату и время без смещения часового пояса */
export const formatWallClockDateTime = (value: string | null | undefined): string => {
    if (!value) return 'Не указано';

    const parts = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2}))?/.exec(value);

    if (!parts) return 'Не указано';

    const date = new Date(`${parts[1]}T${parts[2]}:${parts[3] ?? '00'}Z`);

    return Number.isFinite(date.getTime())
        ? date.toLocaleString(DEFAULT_LOCALE, {
            timeZone: 'UTC',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
        : 'Не указано';
};

/** Показывает краткую дату и время */
export const formatCompactDateTime = (value: string | null | undefined): string => {
    if (!value) return '';

    const date = new Date(value);

    if (!Number.isFinite(date.getTime())) return '';
    return date.toLocaleString('ru-RU', {
        dateStyle: 'short',
        timeStyle: 'short'
    });
};

/** Дополняет часть времени до двух цифр */
const padTimePart = (value: number): string => value.toString().padStart(2, '0');

/** Преобразует время суток в значение поля времени */
export const parseTime24 = (value: string | null | undefined): Date | null => {
    if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return null;

    const [hours, minutes] = value.split(':').map(Number);

    return new Date(2000, 0, 1, hours, minutes, 0, 0);
};

/** Возвращает ключ локального календарного дня */
export const getLocalDayKey = (timestamp: number): string => calendarDateToDay(new Date(timestamp));

/** Возвращает предыдущий календарный день */
const getYesterday = (now: Date): Date => {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    return yesterday;
};

/** Показывает локальное время в 24-часовом формате */
export const formatTime24 = (timestamp: number): string => {
    const date = new Date(timestamp);

    return `${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}`;
};

/** Подписывает календарный день относительно сегодняшнего */
export const formatRelativeDay = (
    timestamp: number,
    now = new Date(),
    locale = DEFAULT_LOCALE,
): string => {
    const dayKey = getLocalDayKey(timestamp);

    if (dayKey === getLocalDayKey(now.getTime())) return 'Сегодня';
    if (dayKey === getLocalDayKey(getYesterday(now).getTime())) return 'Вчера';

    return new Date(timestamp).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
    });
};

/** Показывает дату и время с относительной подписью дня */
export const formatRelativeTimestamp = (
    timestamp: number | null,
    now = new Date(),
    locale = DEFAULT_LOCALE,
): string => {
    if (timestamp === null) return '';

    const date = new Date(timestamp);
    const time = formatTime24(timestamp);
    const dayKey = getLocalDayKey(timestamp);

    if (dayKey === getLocalDayKey(now.getTime())) return `Сегодня в ${time}`;
    if (dayKey === getLocalDayKey(getYesterday(now).getTime())) return `Вчера в ${time}`;

    const dateLabel = date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        ...(date.getFullYear() === now.getFullYear()
            ? {}
            : { year: 'numeric' }),
    });

    return `${dateLabel} в ${time}`;
};

const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

/** Показывает дату и время в UTC */
export const formatUtcDateTime = (value: string | null | undefined): string => {
    const timestamp = Date.parse(value ?? '');

    return Number.isFinite(timestamp)
        ? `${dateTimeFormatter.format(timestamp)} UTC`
        : '—';
};

const moscowFormatter = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
});

/** Показывает дату и время по Москве */
export const formatMoscowDateTime = (value: string | null | undefined): string => {
    if (!value) return '—';

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? '—'
        : moscowFormatter.format(date);
};

const moscowPartsFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

/** Раскладывает московскую дату и время для полей календаря */
export const getMoscowCalendarParts = (value: string): { date: Date; time: string } => {
    const parts = moscowPartsFormatter.formatToParts(new Date(value));
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
        date: new Date(Number(values.year), Number(values.month) - 1, Number(values.day)),
        time: `${values.hour}:${values.minute}`,
    };
};

/** Возвращает дату календаря в формате года, месяца и дня */
export const calendarDateToDay = (date: Date): string => (
    `${date.getFullYear()}-${padTimePart(date.getMonth() + 1)}-${padTimePart(date.getDate())}`
);

/** Читает календарный день из адреса страницы */
export const parseCalendarDay = (value: string | null): Date | null => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    return calendarDateToDay(date) === value
        ? date
        : null;
};

/** Преобразует московские поля даты и времени в UTC */
export const moscowCalendarToUtc = (date: Date, time: string): string => (
    new Date(`${calendarDateToDay(date)}T${time}:00+03:00`).toISOString()
);

/** Показывает календарный день и название месяца */
export const formatCalendarDay = (timestamp: number): string => (
    Number.isFinite(timestamp)
        ? new Date(timestamp).toLocaleDateString(DEFAULT_LOCALE, {
            day: 'numeric',
            month: 'long'
        })
        : ''
);

const moscowTimeFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    timeZone: 'Europe/Moscow',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

const moscowDayFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    timeZone: 'Europe/Moscow',
    day: 'numeric',
    month: 'long',
});

/** Показывает часы и минуты по Москве */
export const formatMoscowTime = (value: string | null | undefined): string => {
    const timestamp = Date.parse(value ?? '');

    return Number.isFinite(timestamp)
        ? moscowTimeFormatter.format(timestamp)
        : '';
};

/** Возвращает ключ московского календарного дня */
export const getMoscowDayKey = (timestamp: number): string => {
    if (!Number.isFinite(timestamp)) return '';

    const parts = moscowPartsFormatter.formatToParts(timestamp);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return `${values.year}-${values.month}-${values.day}`;
};

const moscowShortDayFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
});

/** Показывает время по Москве: сегодня с подписью, в остальные дни с короткой датой */
export const formatMoscowRelativeTimestamp = (
    value: string | null | undefined,
    now = new Date(),
): string => {
    const timestamp = Date.parse(value ?? '');

    if (!Number.isFinite(timestamp)) return '';

    const dayKey = getMoscowDayKey(timestamp);
    const time = moscowTimeFormatter.format(timestamp);

    if (dayKey === getMoscowDayKey(now.getTime())) return `Сегодня ${time}`;

    return `${moscowShortDayFormatter.format(timestamp)} ${time}`;
};

/** Показывает день и месяц по Москве */
export const formatMoscowCalendarDay = (timestamp: number): string => (
    Number.isFinite(timestamp)
        ? moscowDayFormatter.format(timestamp)
        : ''
);


/** Читает полностью введённые локальные дату и время */
export const parseCalendarDateTime = (value: string): Date | null => {
    const match = /^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}:\d{2})$/.exec(value);

    if (!match) return null;

    const date = parseCalendarDay(`${match[3]}-${match[2]}-${match[1]}`);
    const time = parseTime24(match[4]);

    if (!date || !time) return null;

    date.setHours(time.getHours(), time.getMinutes(), 0, 0);

    return date;
};

/** Показывает дату и время города с обозначением смещения относительно Москвы */
export const formatDateTimeAtOffset = (
    value: string | null | undefined,
    utcOffsetMinutes: number = 180,
): string => {
    const timestamp = Date.parse(value ?? '');

    if (!Number.isFinite(timestamp)) return 'Нет данных';

    const date = new Date(timestamp + utcOffsetMinutes * 60_000);
    const moscowOffset = utcOffsetMinutes - 180;
    const hours = Math.floor(Math.abs(moscowOffset) / 60);
    const minutes = Math.abs(moscowOffset) % 60;

    const difference = minutes === 0
        ? String(hours)
        : `${hours}:${padTimePart(minutes)}`;

    const sign = moscowOffset > 0
        ? '+'
        : '-';

    const zone = moscowOffset === 0
        ? 'MSK'
        : `MSK${sign}${difference}`;

    const formatted = date.toLocaleString(DEFAULT_LOCALE, {
        timeZone: 'UTC',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return `${formatted} (${zone})`;
};
